import type { FilterChip, Lookups, TreeLookupItem } from "../../types";
import { renderFilterDetailList } from "../../components/FilterDetailList";
import { mapIdsToNames } from "../../utils/mapIdsToNames";
import { summarizeSelectedTreeItems } from "../../utils/summarizeTreeSelection";
import type { IndustrySlice } from "./slice";

export function getChips(
  slice: IndustrySlice,
  setSlice: (slice: IndustrySlice) => void,
  lookups?: Lookups
): FilterChip[] {
  const chips: FilterChip[] = [];

  const includedCount =
    slice.industryIds.length + slice.sicIds.length + slice.naicsIds.length;
  const excludedCount =
    slice.excludedIds.length +
    slice.excludedSicIds.length +
    slice.excludedNaicsIds.length;

  if (includedCount === 0 && excludedCount === 0) return [];

  const includedTypesCount =
    (slice.industryIds.length > 0 ? 1 : 0) +
    (slice.sicIds.length > 0 ? 1 : 0) +
    (slice.naicsIds.length > 0 ? 1 : 0);

  const excludedTypesCount =
    (slice.excludedIds.length > 0 ? 1 : 0) +
    (slice.excludedSicIds.length > 0 ? 1 : 0) +
    (slice.excludedNaicsIds.length > 0 ? 1 : 0);

  // Included chip
  if (includedCount > 0) {
    const includedHgNames =
      slice.industryIds.length > 0 && lookups?.industries
        ? mapIdsToNames(slice.industryIds, lookups.industries)
        : [];

    const sicItems = (lookups?.sicCodes ?? []) as TreeLookupItem[];
    const naicsItems = (lookups?.naicsCodes ?? []) as TreeLookupItem[];

    const sicSummary =
      slice.sicIds.length > 0 && sicItems.length > 0
        ? summarizeSelectedTreeItems(slice.sicIds, sicItems, {
            subtreeSuffix: " (all subcodes)",
          })
        : undefined;

    const naicsSummary =
      slice.naicsIds.length > 0 && naicsItems.length > 0
        ? summarizeSelectedTreeItems(slice.naicsIds, naicsItems, {
            subtreeSuffix: " (all subcodes)",
          })
        : undefined;

    const sicTooltipNames = sicSummary?.tooltipNames ?? [];
    const naicsTooltipNames = naicsSummary?.tooltipNames ?? [];

    let label: string;
    if (includedTypesCount !== 1) {
      label = `${includedCount} selected`;
    } else if (slice.industryIds.length > 0) {
      label =
        slice.industryIds.length === 1 && includedHgNames[0]
          ? includedHgNames[0]
          : `${slice.industryIds.length} selected`;
    } else if (slice.sicIds.length > 0) {
      label = sicSummary?.label ?? `${slice.sicIds.length} selected`;
    } else {
      label = naicsSummary?.label ?? `${slice.naicsIds.length} selected`;
    }

    const tooltipContent =
      lookups?.industries || lookups?.sicCodes || lookups?.naicsCodes ? (
        <>
          {slice.industryIds.length > 0 &&
            includedHgNames.length > 0 &&
            renderFilterDetailList("HG Industries", includedHgNames)}
          {slice.sicIds.length > 0 &&
            sicTooltipNames.length > 0 &&
            renderFilterDetailList("SIC Codes", sicTooltipNames)}
          {slice.naicsIds.length > 0 &&
            naicsTooltipNames.length > 0 &&
            renderFilterDetailList("NAICS Codes", naicsTooltipNames)}
        </>
      ) : undefined;

    chips.push({
      id: "industry",
      label: `Industries: ${label}`,
      filterKey: "industry" as const,
      onRemove: () =>
        setSlice({
          ...slice,
          industryIds: [],
          sicIds: [],
          naicsIds: [],
        }),
      tooltipContent,
    });
  }

  // Excluded chip
  if (excludedCount > 0) {
    const excludedHgNames =
      slice.excludedIds.length > 0 && lookups?.industries
        ? mapIdsToNames(slice.excludedIds, lookups.industries)
        : [];

    const sicItems = (lookups?.sicCodes ?? []) as TreeLookupItem[];
    const naicsItems = (lookups?.naicsCodes ?? []) as TreeLookupItem[];

    const excludedSicSummary =
      slice.excludedSicIds.length > 0 && sicItems.length > 0
        ? summarizeSelectedTreeItems(slice.excludedSicIds, sicItems, {
            subtreeSuffix: " (all subcodes)",
          })
        : undefined;

    const excludedNaicsSummary =
      slice.excludedNaicsIds.length > 0 && naicsItems.length > 0
        ? summarizeSelectedTreeItems(slice.excludedNaicsIds, naicsItems, {
            subtreeSuffix: " (all subcodes)",
          })
        : undefined;

    const excludedSicTooltipNames = excludedSicSummary?.tooltipNames ?? [];
    const excludedNaicsTooltipNames = excludedNaicsSummary?.tooltipNames ?? [];

    let label: string;
    if (excludedTypesCount !== 1) {
      label = `${excludedCount} selected`;
    } else if (slice.excludedIds.length > 0) {
      label =
        slice.excludedIds.length === 1 && excludedHgNames[0]
          ? excludedHgNames[0]
          : `${slice.excludedIds.length} selected`;
    } else if (slice.excludedSicIds.length > 0) {
      label =
        excludedSicSummary?.label ?? `${slice.excludedSicIds.length} selected`;
    } else {
      label =
        excludedNaicsSummary?.label ??
        `${slice.excludedNaicsIds.length} selected`;
    }

    const tooltipContent =
      lookups?.industries || lookups?.sicCodes || lookups?.naicsCodes ? (
        <>
          {slice.excludedIds.length > 0 &&
            excludedHgNames.length > 0 &&
            renderFilterDetailList("HG Industries", excludedHgNames)}
          {slice.excludedSicIds.length > 0 &&
            excludedSicTooltipNames.length > 0 &&
            renderFilterDetailList("SIC Codes", excludedSicTooltipNames)}
          {slice.excludedNaicsIds.length > 0 &&
            excludedNaicsTooltipNames.length > 0 &&
            renderFilterDetailList("NAICS Codes", excludedNaicsTooltipNames)}
        </>
      ) : undefined;

    chips.push({
      id: "excludedIndustry",
      label: `Excluded Industries: ${label}`,
      filterKey: "industry" as const,
      onRemove: () =>
        setSlice({
          ...slice,
          excludedIds: [],
          excludedSicIds: [],
          excludedNaicsIds: [],
        }),
      tooltipContent,
    });
  }

  return chips;
}

export function getCount(slice: IndustrySlice): number {
  return (
    slice.industryIds.length +
    slice.sicIds.length +
    slice.naicsIds.length +
    slice.excludedIds.length +
    slice.excludedSicIds.length +
    slice.excludedNaicsIds.length
  );
}
